import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, UrlSegment } from '@angular/router';

import { BehaviorSubject, Observable, filter } from 'rxjs';

import { TitleService } from '../title.service';
import { HistoryEntry } from './history-entry';
import { HistoryJournal } from './history-journal';

@Injectable({
  providedIn: 'root'
})
export class HistoryService implements OnDestroy {
  private historyJournal: BehaviorSubject<HistoryJournal>;
  public history: Observable<HistoryJournal>;
  private MaxHistoryLength = 10;

  constructor(
    private router: Router,
    private title: TitleService
  ) {
    this.historyJournal = new BehaviorSubject<HistoryJournal>(new HistoryJournal());
    this.history = this.historyJournal.asObservable();

    //starting page
    this.logHistory(this.title.getTitle(), this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        // No Unsubscribe
        //console.log(`History got event`, event);
        if (event instanceof NavigationEnd) {
          this.logHistory(this.title.getTitle(), event.url);
        }
      });
  }

  getHistory(): HistoryJournal {
    return this.historyJournal.value;
  }

  logHistory(title: string | null = null, url: string | null = null) {
    title = title ? title : this.title.getTitle();
    url = url ? url : this.router?.url;
    const currentHistory = this.historyJournal.getValue();
    const lastEntry = currentHistory.journal[currentHistory.currentIndex];
    //console.log(lastEntry,'last')
    // don't update if new entry matches old history
    const lastUrlTree = lastEntry ? this.router.parseUrl(lastEntry.url) : undefined;
    const lastUrlRoot = lastUrlTree?.root?.children?.['primary']?.['segments']
      ?.map((it: UrlSegment) => it.path)
      .join('/');
    const newUrlTree = url ? this.router.parseUrl(url) : undefined;
    const newUrlRoot = newUrlTree?.root?.children?.['primary']?.['segments']
      ?.map((it: UrlSegment) => it.path)
      .join('/');

    //Don't add Login to history
    if (!url.startsWith('/login') && (!lastEntry || url !== lastEntry?.url)) {
      currentHistory.journal.splice(currentHistory.currentIndex + 1);
      //console.log('currentHistory.journal', lastUrlRoot, newUrlRoot);
      const regex = new RegExp('/');
      if (
        lastUrlRoot == newUrlRoot ||
        //Process Edit redirect
        (lastUrlRoot?.startsWith('processes/') && lastUrlRoot.match(regex)?.length == 1)
      ) {
        //Remove old entry if same root to avoid tab or filter changes affecting history
        currentHistory.journal.pop();
      }
      currentHistory.journal.push(new HistoryEntry(title, url));
      if (currentHistory.journal.length > this.MaxHistoryLength) {
        currentHistory.journal.shift();
      }
      currentHistory.currentIndex = currentHistory.journal.length - 1;
      //console.log(`Adding new history log: ${title} -> ${url}`);
      this.historyJournal.next(currentHistory);
    }
  }

  back(fallback?: string) {
    const currentHistory = this.historyJournal.getValue();
    let dest: string;
    //console.log(`Navigating back`, currentHistory);
    if (currentHistory && currentHistory.canGoBack()) {
      currentHistory.currentIndex = currentHistory.currentIndex - 1;
      this.historyJournal.next(currentHistory);
      dest = currentHistory.journal[currentHistory.currentIndex]?.url;
    } else if (fallback) {
      dest = fallback;
    } else {
      const currentUrl = currentHistory.journal[currentHistory.currentIndex]?.url;
      const routeEnd = currentUrl?.lastIndexOf('/');
      dest = routeEnd > 0 ? currentUrl.slice(0, routeEnd) : '/';
      //On wizards, remove an extra level
      if (dest.endsWith('wizard')) dest = dest.slice(0, dest.lastIndexOf('/'));
      // console.log(`Current URL: ${currentUrl} to ${newUrl}`);
    }
    //console.log(`Navigating to ${dest}`);
    this.router.navigateByUrl(dest);
  }

  goTo(id: string) {
    const currentHistory = this.historyJournal.getValue();
    const targetIndex = currentHistory.journal.findIndex((entry) => entry.id === id);
    if (targetIndex >= 0) {
      currentHistory.currentIndex = targetIndex;
      this.historyJournal.next(currentHistory);
      // console.log(
      //   `Navigating to ${currentHistory.journal[currentHistory.currentIndex].url}`,
      //   this.historyJournal.getValue()
      // );
      this.router.navigateByUrl(currentHistory.journal[currentHistory.currentIndex].url);
    }
  }

  remove(url: string) {
    if (!url.startsWith('/')) {
      url = '/' + url;
    }
    //console.log(`History Removing ${url}`);
    const currentHistory = this.historyJournal.getValue();
    const toBeRemoved = currentHistory.journal.filter((entry) => entry.url === url);
    if (toBeRemoved) {
      const currentID = currentHistory.journal[currentHistory.currentIndex].id;
      toBeRemoved.forEach((entry) => {
        const removeIndex = currentHistory.journal.findIndex((target) => target.id === entry.id);
        if (removeIndex >= 0) {
          //console.log(`History Removing ${url} from index of ${removeIndex}`, currentHistory.journal);
          currentHistory.journal.splice(removeIndex, 1);
        }
      });
      currentHistory.currentIndex = currentHistory.journal.findIndex((entry) => entry.id === currentID);
    }
    this.historyJournal.next(currentHistory);
  }

  public updateTitle(template: string, value: string, prefix: string | null) {
    // console.log(`History is updating tile`, this.historyJournal.getValue());
    this.title.updateTitle(template, value, prefix);
    const currentJournal = this.historyJournal.getValue();
    if (currentJournal && currentJournal.journal.length > 0) {
      if (currentJournal.journal[currentJournal.currentIndex]?.url === this.router.url) {
        /*
                 console.log(`History is updating current url '${
                    currentJournal.journal[currentJournal.currentIndex].url} with new title ${
                    this.title.getTitle()} since current url is ${this.router.url}`, this.router);
                */
        currentJournal.journal[currentJournal.currentIndex].title = this.title.getTitle();
        this.historyJournal.next(currentJournal);
      } else {
        this.logHistory();
      }
    } else {
      this.logHistory();
    }
  }

  ngOnDestroy(): void {
    this.historyJournal.complete();
  }
}
